import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { getUserById, getUserSettings, getUserVehicles } from '../services/userService';
import { AuthUser, UserSettings, Vehicle } from '../services/userService';

const UserDetail = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(true);
  const [vehiclesLoading, setVehiclesLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [vehiclesError, setVehiclesError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const userData = await getUserById(userId);
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user details:', error);
        setError('Failed to load user details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const fetchUserSettings = async () => {
      if (!userId) return;

      try {
        setSettingsLoading(true);
        const settings = await getUserSettings(userId);
        setUserSettings(settings);
      } catch (error) {
        console.error('Error fetching user settings:', error);
        setSettingsError('Failed to load user settings. Please try again.');
      } finally {
        setSettingsLoading(false);
      }
    };

    const fetchUserVehicles = async () => {
      if (!userId) return;

      try {
        setVehiclesLoading(true);
        const userVehicles = await getUserVehicles(userId);
        setVehicles(userVehicles);
      } catch (error) {
        console.error('Error fetching user vehicles:', error);
        setVehiclesError('Failed to load user vehicles. Please try again.');
      } finally {
        setVehiclesLoading(false);
      }
    };

    fetchUserData();
    fetchUserSettings();
    fetchUserVehicles();
  }, [userId]);

  const handleBackClick = () => {
    navigate('/users');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatSettingKey = (key: string): string => {
    // Convert camelCase to readable text
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const formatSettingValue = (value: any): string | React.ReactNode => {
    if (value === null || value === undefined) return 'N/A';

    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Handle dates (ISO strings or timestamps)
    if (typeof value === 'string' && !isNaN(Date.parse(value)) && value.includes('T')) {
      return new Date(value).toLocaleString();
    }

    if (typeof value === 'number' && value > 1000000000000 && value < 9999999999999) {
      // Likely a timestamp in milliseconds
      return new Date(value).toLocaleString();
    }

    // Handle objects and arrays
    if (typeof value === 'object') {
      return (
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 1,
            bgcolor: 'grey.100',
            borderRadius: 1,
            fontSize: '0.875rem',
            overflow: 'auto',
            fontFamily: 'monospace'
          }}
        >
          {JSON.stringify(value, null, 2)}
        </Box>
      );
    }

    return value.toString();
  };

  const getVehicleIcon = (type: number) => {
    switch (type) {
      case 1:
        return <DirectionsBusIcon />;
      case 2:
        return <LocalShippingIcon />;
      case 3:
        return <LocalShippingOutlinedIcon />;
      case 4:
        return <AirportShuttleIcon />;
      case 5:
        return <DriveEtaIcon />;
      default:
        return <DirectionsCarIcon />;
    }
  };

  const getVehicleTypeLabel = (type: number): string => {
    switch (type) {
      case 1:
        return 'Bus';
      case 2:
        return 'Truck';
      case 3:
        return 'Semi Truck';
      case 4:
        return 'Caravan';
      case 5:
        return 'Car';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackClick} 
          sx={{ mt: 2 }}
        >
          Back to Users
        </Button>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box p={3}>
        <Alert severity="warning">User not found</Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackClick} 
          sx={{ mt: 2 }}
        >
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackClick} 
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">User Details</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* User Overview Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="User Overview" 
              subheader={
                <Chip 
                  color={user.disabled ? "error" : "success"} 
                  size="small" 
                  label={user.disabled ? "Disabled" : "Active"} 
                  sx={{ mt: 1 }}
                />
              } 
            />
            <CardContent>
              <List>
                <ListItem divider>
                  <ListItemText 
                    primary="User ID" 
                    secondary={user.uid} 
                  />
                </ListItem>
                <ListItem divider>
                  <ListItemText 
                    primary="Email" 
                    secondary={
                      <Box display="flex" alignItems="center">
                        {user.email}
                        {user.emailVerified && (
                          <Chip 
                            label="Verified" 
                            color="primary" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    } 
                  />
                </ListItem>
                <ListItem divider>
                  <ListItemText 
                    primary="Display Name" 
                    secondary={user.displayName || 'Not set'}
                  />
                </ListItem>
                <ListItem divider>
                  <ListItemText 
                    primary="Phone Number" 
                    secondary={user.phoneNumber || 'Not set'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Authentication Details Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Authentication Details" />
            <CardContent>
              <List>
                <ListItem divider>
                  <ListItemText 
                    primary="Created At" 
                    secondary={formatDate(user.creationTime)}
                  />
                </ListItem>
                <ListItem divider>
                  <ListItemText 
                    primary="Last Sign In" 
                    secondary={formatDate(user.lastSignInTime)}
                  />
                </ListItem>
                <ListItem divider>
                  <ListItemText 
                    primary="Email Verified" 
                    secondary={user.emailVerified ? 'Yes' : 'No'}
                  />
                </ListItem>
                <ListItem divider>
                  <ListItemText 
                    primary="Account Status" 
                    secondary={user.disabled ? 'Disabled' : 'Active'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Settings Card */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          User Settings
        </Typography>
        <Card>
          <CardHeader 
            title="User Settings" 
            avatar={<SettingsIcon />}
            subheader="Settings from Firebase Realtime Database"
          />
          <CardContent>
            {settingsLoading ? (
              <Box sx={{ width: '100%' }}>
                <Skeleton animation="wave" height={40} />
                <Skeleton animation="wave" height={40} />
                <Skeleton animation="wave" height={40} />
              </Box>
            ) : settingsError ? (
              <Alert severity="error">{settingsError}</Alert>
            ) : !userSettings ? (
              <Alert severity="info">No settings found for this user</Alert>
            ) : (
              <Box>
                <List>
                  {Object.entries(userSettings).map(([key, value]) => (
                    <ListItem key={key} divider>
                      <ListItemText
                        primary={formatSettingKey(key)}
                        secondary={
                          <Box sx={{ wordBreak: 'break-word' }}>
                            {formatSettingValue(value)}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* User Vehicles Card */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          User Vehicles
        </Typography>
        <Card>
          <CardHeader
            title="Vehicles"
            avatar={<DirectionsCarIcon />}
            subheader="Vehicles from Firebase Realtime Database (truckDB)"
          />
          <CardContent>
            {vehiclesLoading ? (
              <Box sx={{ width: '100%' }}>
                <Skeleton animation="wave" height={60} />
                <Skeleton animation="wave" height={60} />
                <Skeleton animation="wave" height={60} />
              </Box>
            ) : vehiclesError ? (
              <Alert severity="error">{vehiclesError}</Alert>
            ) : vehicles.length === 0 ? (
              <Alert severity="info">No vehicles found for this user</Alert>
            ) : (
              <Box>
                {vehicles.map((vehicle) => (
                  <Accordion key={vehicle.id} sx={{ mb: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`vehicle-${vehicle.id}-content`}
                      id={`vehicle-${vehicle.id}-header`}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        {vehicle.type && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                            {getVehicleIcon(Number(vehicle.type))}
                          </Box>
                        )}
                        <Typography sx={{ fontWeight: 'bold', minWidth: '120px' }}>
                          ID: {vehicle.id}
                        </Typography>
                        {vehicle.name && (
                          <Typography sx={{ color: 'text.secondary' }}>
                            {vehicle.name}
                          </Typography>
                        )}
                        {vehicle.type && (
                          <Chip
                            label={getVehicleTypeLabel(Number(vehicle.type))}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 'auto' }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List>
                        {Object.entries(vehicle)
                          .filter(([key]) => key !== 'id' && key !== 'name' && key !== 'type')
                          .map(([key, value]) => (
                            <ListItem key={key} divider>
                              <ListItemText
                                primary={formatSettingKey(key)}
                                secondary={
                                  <Box sx={{ wordBreak: 'break-word' }}>
                                    {formatSettingValue(value)}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default UserDetail;
